import { useRef } from 'react';
import { Button } from '@givebutter/primitives/button';
import { faArrowDownToBracket } from '@fortawesome/pro-regular-svg-icons';
import { faFilePdf } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as DropdownMenu from 'components/dropdown-menu';
import * as SettingsCard from 'components/settings-card';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';

const CampaignSharingQrCode = () => {
    const { data: campaign } = useCurrentCampaign();
    const qr = campaign?.qr;
    const imageRef = useRef<HTMLImageElement>(null);

    const downloadAs = (type: string) => {
        const svg = imageRef?.current;

        if (svg) {
            const height = svg.height * 2;
            const width = svg.width * 2;
            const svgData = svg.src;
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');

            canvas.width = width;
            canvas.height = height;

            const image = new Image();

            image.onload = () => {
                context?.clearRect(0, 0, width, height);
                context?.drawImage(image, 0, 0, width, height);
                const pngData = canvas.toDataURL('image/' + type);

                downloadImage(pngData);
            };

            image.src = svgData;
        }
    };

    const downloadImage = (imageData: string) => {
        const event = new MouseEvent('click', {
            view: window,
            bubbles: false,
            cancelable: true,
        });

        const a = document.createElement('a');
        a.setAttribute('download', 'qr-code');
        a.setAttribute('href', imageData);
        a.setAttribute('target', '_blank');
        a.dispatchEvent(event);
    };

    const downloadSvg = () => {
        if (imageRef.current) {
            downloadImage(imageRef.current.src);
        }
    };

    return (
        <SettingsCard.Root>
            <SettingsCard.LeftSide>
                <SettingsCard.Heading>Scan-to-donate</SettingsCard.Heading>
                <p className={'text-sm text-gray-700'}>
                    Print and scan this QR code to be linked directly to your campaign page.{' '}
                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={'https://help.givebutter.com/en/articles/3471280-scan-to-give'}
                    >
                        Read the guide
                    </a>
                    .
                </p>
            </SettingsCard.LeftSide>
            <SettingsCard.RightSide>
                {qr ? (
                    <div className={'flex max-w-[9rem] flex-col gap-2'}>
                        <img alt="QR code" src={qr.image} ref={imageRef} />
                        <a
                            className={
                                'flex items-center justify-center gap-2 rounded-md bg-gray-200 py-1.5 text-sm text-black'
                            }
                            href={qr.pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <FontAwesomeIcon icon={faFilePdf} />
                            Print PDF
                        </a>
                        <div className={'flex items-center justify-center gap-2'}>
                            <p className={'text-sm font-medium'}>Download as</p>
                            <DropdownMenu.Root>
                                <DropdownMenu.Trigger asChild={true}>
                                    <Button
                                        size={'sm'}
                                        variant={'outline'}
                                        color={'neutral'}
                                        shape={'square'}
                                        aria-label="Download"
                                    >
                                        <FontAwesomeIcon icon={faArrowDownToBracket} />
                                    </Button>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content>
                                    <DropdownMenu.Item onClick={() => downloadAs('png')}>
                                        PNG
                                    </DropdownMenu.Item>
                                    <DropdownMenu.Item onClick={() => downloadAs('jpeg')}>
                                        JPG
                                    </DropdownMenu.Item>
                                    <DropdownMenu.Item onClick={downloadSvg}>SVG</DropdownMenu.Item>
                                    <a href={qr.pdf} rel="noopener noreferrer" target={'_blank'}>
                                        <DropdownMenu.Item
                                            className={'text-gray-800 hover:!text-gray-800'}
                                        >
                                            PDF
                                        </DropdownMenu.Item>
                                    </a>
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        </div>
                    </div>
                ) : (
                    <div className="text-red-500">Error loading QR code</div>
                )}
            </SettingsCard.RightSide>
        </SettingsCard.Root>
    );
};

export { CampaignSharingQrCode };
